// src/core/validation/validation.ts
export interface ValidationResult {
  valid: boolean;
  message: string;
}

/* Generic name validator */
export const validateName = (
  value: string,
  fieldLabel: string
): ValidationResult => {
  const clean = value.trim();

  const htmlCheck = validateNoHTML(clean, fieldLabel);
  if (!htmlCheck.valid) return htmlCheck;

  if (!clean) return { valid: false, message: `${fieldLabel} is required.` };

  const allowedRegex = /^[A-Za-z0-9@$&\/\-_. ]+$/;
  if (!allowedRegex.test(clean)) {
    return {
      valid: false,
      message: `${fieldLabel} can only contain letters, numbers, spaces, and @$&/-_.`,
    };
  }

  if (clean.length < 3)
    return {
      valid: false,
      message: `${fieldLabel} must have at least 3 characters.`,
    };
  if (clean.length > 100)
    return {
      valid: false,
      message: `${fieldLabel} cannot exceed 100 characters.`,
    };
  return { valid: true, message: "" };
};

/* Generic number validator */
export const validateNumber = (
  value: string | number,
  fieldLabel: string,
  options?: { min?: number; max?: number; allowDecimal?: boolean }
): ValidationResult => {
  const val = String(value).trim();
  if (!val) return { valid: false, message: `${fieldLabel} is required.` };

  const regex = options?.allowDecimal ? /^[0-9]+(\.[0-9]{1,2})?$/ : /^[0-9]+$/;

  if (!regex.test(val))
    return { valid: false, message: `${fieldLabel} must be a valid number.` };

  const numVal = Number(val);

  if (options?.min !== undefined && numVal < options.min)
    return {
      valid: false,
      message: `${fieldLabel} must be at least ${options.min}.`,
    };

  if (options?.max !== undefined && numVal > options.max)
    return {
      valid: false,
      message: `${fieldLabel} cannot exceed ${options.max}.`,
    };

  return { valid: true, message: "" };
};

/* Generic select validator */
export const validateSelect = (
  value: string,
  fieldLabel: string
): ValidationResult => {
  if (!value)
    return { valid: false, message: `Please select a ${fieldLabel}.` };
  return { valid: true, message: "" };
};

export const validateRequiredString = (
  value: string,
  fieldLabel: string
): ValidationResult => {
  if (!value || value.trim() === "") {
    return { valid: false, message: `${fieldLabel} is required.` };
  }
  const clean = value.trim();
  const allowedRegex = /^[A-Za-z0-9@$&\/\-_ ]+$/;

  if (!allowedRegex.test(clean)) {
    return {
      valid: false,
      message: `${fieldLabel} can only contain letters, numbers, spaces, and @$&/-_.`,
    };
  }
  return { valid: true, message: "" };
};

/* Target specific validators */
export const validateSalary = (salary: string | number): ValidationResult =>
  validateNumber(salary, "Salary", { min: 1, allowDecimal: false });

export const validateTargetValue = (
  target: string | number
): ValidationResult =>
  validateNumber(target, "Target", { min: 1, allowDecimal: false });

export const validateIncentivePercentage = (
  percentage: string | number
): ValidationResult =>
  validateNumber(percentage, "Incentive Percentage", {
    min: 0,
    max: 100,
    allowDecimal: true,
  });

/* Name helpers (keep if you still need them) */
export const validateLeadsource = (name: string) =>
  validateName(name, "Lead source");
export const validateLeadindustry = (name: string) =>
  validateName(name, "Lead industry");
export const validateLeadstage = (name: string) =>
  validateName(name, "Lead stage");
export const validateLeadtype = (name: string) =>
  validateName(name, "Lead type");
export const validateProducttype = (name: string) =>
  validateName(name, "Product type");
export const validateCustomertype = (name: string) =>
  validateName(name, "Customer type");
export const validateDealsource = (name: string) =>
  validateName(name, "Deal source");
export const validateProductName = (name: string) =>
  validateName(name, "Product");

/* Product helpers */
export const validateProductPrice = (price: string): ValidationResult =>
  validateNumber(price, "Price", { min: 1, allowDecimal: true });

export const validateProductDescription = (
  description: string
): ValidationResult => {
  const val = description.trim();
  if (!val) return { valid: false, message: "Description is required." };
  if (val.length < 5)
    return {
      valid: false,
      message: "Description must have at least 5 characters.",
    };
  if (val.length > 150)
    return {
      valid: false,
      message: "Description cannot exceed 150 characters.",
    };

  // ❌ Block HTML tags like <tag>, </tag>, <script>, etc.
  const htmlRegex = /<\/?[^>]+>/g;
  if (htmlRegex.test(val)) {
    return {
      valid: false,
      message: "Description cannot contain HTML or script tags.",
    };
  }

  // ✔ Allow only letters, numbers, spaces, and @$&/-_
  const allowedRegex = /^[A-Za-z0-9 @$&\/\-_]+$/;
  if (!allowedRegex.test(val)) {
    return {
      valid: false,
      message:
        "Description can only contain letters, numbers, spaces, and @$&/-_.",
    };
  }

  return { valid: true, message: "" };
};

export const validateLeadStatus = (status: string): ValidationResult => {
  if (!status) return { valid: false, message: "Please select a status." };
  if (!["Active", "Inactive"].includes(status))
    return { valid: false, message: "Invalid status selected." };
  return { valid: true, message: "" };
};

/* Prevent HTML or Script Injection */
export const validateNoHTML = (
  value: string,
  fieldLabel: string
): ValidationResult => {
  const clean = value.trim();

  // Regex to detect ANY HTML tag like < > </ >
  const htmlRegex = /<\/?[^>]+>/g;

  if (htmlRegex.test(clean)) {
    return {
      valid: false,
      message: `${fieldLabel} cannot contain HTML or script tags.`,
    };
  }

  return { valid: true, message: "" };
};

export interface UserSelectFormData {
  calculateUserId: string;
}

export interface UserSelectErrors {
  calculateUserId?: string;
}

export const validateUserSelect = (
  data: UserSelectFormData
): UserSelectErrors => {
  const errors: UserSelectErrors = {};

  if (!data.calculateUserId || data.calculateUserId.trim() === "") {
    errors.calculateUserId = "Please select a user";
  }

  return errors;
};

/* ================================
   Additional Lead Source Validators
   ================================ */

/* Prevent duplicate lead source names */
export const validateLeadSourceDuplicate = (
  value: string,
  existingNames: string[]
): ValidationResult => {
  const clean = value.trim().toLowerCase();

  if (existingNames.map((n) => n.toLowerCase()).includes(clean)) {
    return { valid: false, message: "Lead source already exists." };
  }

  return { valid: true, message: "" };
};

/* Prevent double spaces */
export const validateNoDoubleSpacesLead = (
  value: string,
  fieldName: string = "This field"
): ValidationResult => {
  if (/\s{2,}/.test(value)) {
    return {
      valid: false,
      message: `${fieldName} cannot contain consecutive spaces.`,
    };
  }
  return { valid: true, message: "" };
};

/* Prevent only symbols */
export const validateNotOnlySymbolsLead = (
  value: string,
  fieldName: string = "This field"
): ValidationResult => {
  if (/^[^A-Za-z0-9]+$/.test(value.trim())) {
    return {
      valid: false,
      message: `${fieldName} cannot contain only symbols.`,
    };
  }
  return { valid: true, message: "" };
};

/* Prevent long repeated characters like aaaaa or !!!! */
export const validateNoRepeatedCharactersLead = (
  value: string,
  fieldName: string = "This field"
): ValidationResult => {
  if (/(.)\1{3,}/.test(value)) {
    return {
      valid: false,
      message: `${fieldName} contains too many repeated characters.`,
    };
  }
  return { valid: true, message: "" };
};

/* REUSABLE: Prevent starting or ending with special characters - works for ALL modules */
export const validateNoEdgeSymbols = (
  value: string,
  fieldName: string = "This field"
): ValidationResult => {
  if (/^[^A-Za-z0-9]/.test(value) || /[^A-Za-z0-9]$/.test(value)) {
    return {
      valid: false,
      message: `${fieldName} cannot start or end with special characters.`,
    };
  }
  return { valid: true, message: "" };
};

/* Prevent names that are numbers only */
export const validateNotOnlyNumbersLead = (
  value: string,
  fieldName: string = "This field"
): ValidationResult => {
  if (/^[0-9]+$/.test(value.trim())) {
    return { valid: false, message: `${fieldName} cannot be only numbers.` };
  }
  return { valid: true, message: "" };
};
/* Prevent names that are common spam words */

/* Prevent duplicate lead industry names */
export const validateLeadIndustryDuplicate = (
  value: string,
  existingNames: string[]
): ValidationResult => {
  const clean = value.trim().toLowerCase();
  if (existingNames.map((n) => n.toLowerCase()).includes(clean)) {
    return { valid: false, message: "Business type already exists." };
  }
  return { valid: true, message: "" };
};

/* Prevent duplicate lead stage names */
export const validateLeadStageDuplicate = (
  value: string,
  existingNames: string[]
): ValidationResult => {
  const clean = value.trim().toLowerCase();
  if (existingNames.map((n) => n.toLowerCase()).includes(clean)) {
    return { valid: false, message: "Lead stage already exists." };
  }
  return { valid: true, message: "" };
};

/* Prevent duplicate lead type names */
export const validateLeadTypeDuplicate = (
  value: string,
  existingNames: string[]
): ValidationResult => {
  const clean = value.trim().toLowerCase();
  if (existingNames.map((n) => n.toLowerCase()).includes(clean)) {
    return { valid: false, message: "Lead type already exists." };
  }
  return { valid: true, message: "" };
};

/* Prevent duplicate product type names */
export const validateProductTypeDuplicate = (
  value: string,
  existingNames: string[]
): ValidationResult => {
  const clean = value.trim().toLowerCase();
  if (existingNames.map((n) => n.toLowerCase()).includes(clean)) {
    return { valid: false, message: "Product type already exists." };
  }
  return { valid: true, message: "" };
};

/* Prevent duplicate customer potential names */
export const validateCustomerPotentialDuplicate = (
  value: string,
  existingNames: string[]
): ValidationResult => {
  const clean = value.trim().toLowerCase();
  if (existingNames.map((n) => n.toLowerCase()).includes(clean)) {
    return { valid: false, message: "Customer potential already exists." };
  }
  return { valid: true, message: "" };
};

/* Prevent duplicate deal source names */
export const validateDealSourceDuplicate = (
  value: string,
  existingNames: string[]
): ValidationResult => {
  const clean = value.trim().toLowerCase();
  if (existingNames.map((n) => n.toLowerCase()).includes(clean)) {
    return { valid: false, message: "Deal source already exists." };
  }
  return { valid: true, message: "" };
};

/* Prevent duplicate product names */
export const validateProductNameDuplicate = (
  value: string,
  existingNames: string[]
): ValidationResult => {
  const clean = value.trim().toLowerCase();
  if (existingNames.map((n) => n.toLowerCase()).includes(clean)) {
    return { valid: false, message: "Product name already exists." };
  }
  return { valid: true, message: "" };
};
// src/core/validation/validation.ts

// ... existing code ...

/* ================================
   Profile-Specific Validators
   ================================ */

/* First & Last Name (strict: no symbols, min 2, max 50) */
export const validatePersonName = (
  value: string,
  fieldLabel: string
): ValidationResult => {
  const clean = value.trim();
  if (!clean) return { valid: false, message: `${fieldLabel} is required.` };
  const htmlCheck = validateNoHTML(clean, fieldLabel);
  if (!htmlCheck.valid) return htmlCheck;
  const doubleSpace = validateNoDoubleSpacesLead(clean, fieldLabel);
  if (!doubleSpace.valid) return doubleSpace;
  const onlySymbols = validateNotOnlySymbolsLead(clean, fieldLabel);
  if (!onlySymbols.valid) return onlySymbols;
  const repeated = validateNoRepeatedCharactersLead(clean, fieldLabel);
  if (!repeated.valid) return repeated;
  const edge = validateNoEdgeSymbols(clean, fieldLabel);
  if (!edge.valid) return edge;
  const allowedRegex = /^[A-Za-z\s]+$/;
  if (!allowedRegex.test(clean)) {
    return {
      valid: false,
      message: `${fieldLabel} can only contain letters and spaces.`,
    };
  }
  if (clean.length < 3)
    return {
      valid: false,
      message: `${fieldLabel} must have at least 3 characters.`,
    };
  if (clean.length > 50)
    return {
      valid: false,
      message: `${fieldLabel} cannot exceed 50 characters.`,
    };
  return { valid: true, message: "" };
};

export const validateAddress = (
  value: string,
  fieldLabel: string
): ValidationResult => {
  const clean = value.trim();
  if (!clean) return { valid: false, message: `${fieldLabel} is required.` };
  const htmlCheck = validateNoHTML(clean, fieldLabel);
  if (!htmlCheck.valid) return htmlCheck;
  const doubleSpace = validateNoDoubleSpacesLead(clean, fieldLabel);
  if (!doubleSpace.valid) return doubleSpace;
  const allowedRegex = /^[A-Za-z0-9\s\/\-_,.]+$/;
  if (!allowedRegex.test(clean)) {
    return {
      valid: false,
      message: `${fieldLabel} can only contain letters, numbers, spaces, / - _ , .`,
    };
  }
  if (clean.length < 5)
    return {
      valid: false,
      message: `${fieldLabel} must have at least 5 characters.`,
    };
  if (clean.length > 200)
    return {
      valid: false,
      message: `${fieldLabel} cannot exceed 200 characters.`,
    };
  return { valid: true, message: "" };
};

export const validateLocationName = (
  value: string,
  fieldLabel: string
): ValidationResult => {
  const clean = value.trim();
  if (!clean) return { valid: false, message: `${fieldLabel} is required.` };
  const htmlCheck = validateNoHTML(clean, fieldLabel);
  if (!htmlCheck.valid) return htmlCheck;
  const doubleSpace = validateNoDoubleSpacesLead(clean, fieldLabel);
  if (!doubleSpace.valid) return doubleSpace;
  const onlySymbols = validateNotOnlySymbolsLead(clean, fieldLabel);
  if (!onlySymbols.valid) return onlySymbols;
  const repeated = validateNoRepeatedCharactersLead(clean, fieldLabel);
  if (!repeated.valid) return repeated;
  const edge = validateNoEdgeSymbols(clean, fieldLabel);
  if (!edge.valid) return edge;
  const allowedRegex = /^[A-Za-z\s]+$/;
  if (!allowedRegex.test(clean)) {
    return {
      valid: false,
      message: `${fieldLabel} can only contain letters and spaces.`,
    };
  }
  if (clean.length < 2)
    return {
      valid: false,
      message: `${fieldLabel} must have at least 2 characters.`,
    };
  if (clean.length > 60)
    return {
      valid: false,
      message: `${fieldLabel} cannot exceed 60 characters.`,
    };
  return { valid: true, message: "" };
};

export const validatePostalCode = (
  value: string,
  fieldLabel: string
): ValidationResult => {
  const clean = value.trim();
  if (!clean) return { valid: false, message: `${fieldLabel} is required.` };
  if (!/^[0-9]+$/.test(clean)) {
    return { valid: false, message: `${fieldLabel} can only contain numbers.` };
  }
  if (clean.length > 15) {
    return {
      valid: false,
      message: `${fieldLabel} cannot exceed 15 characters.`,
    };
  }
  return { valid: true, message: "" };
};
